<template>
  <canvas class="doodle-canvas" :class="{ 'doodle-canvas__text': doodleTool === 'text' }" />
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
const emits = defineEmits<{
  (e: 'escapeKeyup')
  (e: 'mousePosition', value: { x: number; y: number })
  (e: 'showTempCanvas', value: boolean)
  (e: 'textPosition', value: { x: number; y: number })
}>()
const props = defineProps<{
  doodleBrushSize: number
  doodleColor: string
  doodleLayerId: string
  doodleTool: string
}>()
const ctx = ref<CanvasRenderingContext2D | null>(null)
const doodleBrushSize = computed(() => props.doodleBrushSize)
const doodleColor = computed(() => props.doodleColor)
const doodleCanvas = ref()
const doodleTool = computed(() => props.doodleTool)
const lastHeight = ref(0)
const lastWidth = ref(0)
const lastX = ref()
const lastY = ref(-1)
const mouseDown = ref(0)
const mouseX = ref()
const mouseY = ref()
const startX = ref(0)
const startY = ref(0)
const tempCanvas = ref()
const tempCtx = ref<CanvasRenderingContext2D | null>(null)

function drawCircle(ctx, tempCtx, size) {
  if (!tempCtx) return
  if (mouseDown.value === 1) {
    // During mouse down, a temporary layer is used to continuously draw and clear during mouse movement
    const width = mouseX.value - startX.value
    const height = mouseY.value - startY.value
    tempCtx.globalCompositeOperation = 'source-over'
    tempCtx.strokeStyle = doodleColor.value
    tempCtx.lineCap = 'round'
    tempCtx.lineWidth = size
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    const kappa = 0.5522848
    const ox = (width / 2) * kappa
    const oy = (height / 2) * kappa
    const xe = startX.value + width
    const ye = startY.value + height
    const xm = startX.value + width / 2
    const ym = startY.value + height / 2
    tempCtx.beginPath()
    tempCtx.moveTo(startX.value, ym)
    tempCtx.bezierCurveTo(startX.value, ym - oy, xm - ox, startY.value, xm, startY.value)
    tempCtx.bezierCurveTo(xm + ox, startY.value, xe, ym - oy, xe, ym)
    tempCtx.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye)
    tempCtx.bezierCurveTo(xm - ox, ye, startX.value, ym + oy, startX.value, ym)
    tempCtx.stroke()
    lastWidth.value = width
    lastHeight.value = height
  } else {
    // After the mouse button is released, the temporary drawing is cleared and the final drawing is produced on the main canvas
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = doodleColor.value
    ctx.lineCap = 'round'
    ctx.lineWidth = size
    const kappa = 0.5522848
    const ox = (lastWidth.value / 2) * kappa
    const oy = (lastHeight.value / 2) * kappa
    const xe = startX.value + lastWidth.value
    const ye = startY.value + lastHeight.value
    const xm = startX.value + lastWidth.value / 2
    const ym = startY.value + lastHeight.value / 2
    ctx.beginPath()
    ctx.moveTo(startX.value, ym)
    ctx.bezierCurveTo(startX.value, ym - oy, xm - ox, startY.value, xm, startY.value)
    ctx.bezierCurveTo(xm + ox, startY.value, xe, ym - oy, xe, ym)
    ctx.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye)
    ctx.bezierCurveTo(xm - ox, ye, startX.value, ym + oy, startX.value, ym)
    ctx.stroke()
  }
}
function drawEraser(ctx, x, y, size) {
  if (lastX.value == -1) {
    lastX.value = x
    lastY.value = y
  }
  ctx.globalCompositeOperation = 'destination-out'
  ctx.strokeStyle = '#ffffff'
  ctx.fillStyle = '#ffffff'
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(lastX.value, lastY.value)
  ctx.lineTo(x, y)
  ctx.lineWidth = size
  ctx.stroke()
  ctx.closePath()
  lastX.value = x
  lastY.value = y
}
function drawFree(ctx, x, y, size) {
  if (lastX.value == -1) {
    lastX.value = x
    lastY.value = y
  }
  ctx.globalCompositeOperation = 'source-over'
  ctx.strokeStyle = doodleColor.value
  ctx.lineCap = 'round'
  ctx.lineWidth = size
  ctx.beginPath()
  ctx.moveTo(lastX.value, lastY.value)
  ctx.lineTo(x, y)
  ctx.stroke()
  ctx.closePath()
  lastX.value = x
  lastY.value = y
}
function drawImage(img, x, y) {
  if (!ctx.value) return
  ctx.value.globalCompositeOperation = 'source-over'
  ctx.value.drawImage(img, x, y)
}
function drawLine(ctx, tempCtx, x, y, size) {
  if (!tempCtx) return
  if (mouseDown.value === 1) {
    // During mouse down, a temporary layer is used to continuously draw and clear during mouse movement
    if (lastX.value == -1) {
      lastX.value = x
      lastY.value = y
    }
    tempCtx.globalCompositeOperation = 'source-over'
    tempCtx.strokeStyle = doodleColor.value
    tempCtx.lineCap = 'round'
    tempCtx.lineWidth = size
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    tempCtx.beginPath()
    tempCtx.moveTo(startX.value, startY.value)
    tempCtx.lineTo(x, y)
    tempCtx.stroke()
    tempCtx.closePath()
    lastX.value = x
    lastY.value = y
  } else {
    // After the mouse button is released, the temporary drawing is cleared and the final drawing is produced on the main canvas
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = doodleColor.value
    ctx.lineCap = 'round'
    ctx.lineWidth = size
    ctx.beginPath()
    ctx.moveTo(startX.value, startY.value)
    ctx.lineTo(lastX.value, lastY.value)
    ctx.stroke()
    ctx.closePath()
  }
}
function drawRectangle(ctx, tempCtx, size) {
  if (!tempCtx) return
  if (mouseDown.value === 1) {
    // During mouse down, a temporary layer is used to continuously draw and clear during mouse movement
    const width = mouseX.value - startX.value
    const height = mouseY.value - startY.value
    tempCtx.globalCompositeOperation = 'source-over'
    tempCtx.strokeStyle = doodleColor.value
    tempCtx.lineCap = 'square'
    tempCtx.lineWidth = size
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    tempCtx.strokeRect(startX.value, startY.value, width, height)
    lastWidth.value = width
    lastHeight.value = height
  } else {
    // After the mouse button is released, the temporary drawing is cleared and the final drawing is produced on the main canvas
    tempCtx.clearRect(0, 0, doodleCanvas.value.width, doodleCanvas.value.height)
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = doodleColor.value
    ctx.lineWidth = size
    ctx.strokeRect(startX.value, startY.value, lastWidth.value, lastHeight.value)
  }
}
function getMousePos(e) {
  if (e.offsetX) {
    mouseX.value = e.offsetX
    mouseY.value = e.offsetY
  } else if (e.layerX) {
    mouseX.value = e.layerX
    mouseY.value = e.layerY
  }
}
function handleLayerKeyup(event) {
  if (event.key === 'Escape') {
    emits('escapeKeyup')
  }
}
function initializeDoodle() {
  doodleCanvas.value = document.getElementById(props.doodleLayerId)
  doodleCanvas.value.width = window.innerWidth
  doodleCanvas.value.height = window.innerHeight
  if (doodleCanvas.value.getContext) ctx.value = doodleCanvas.value.getContext('2d')
  if (ctx.value) {
    doodleCanvas.value.addEventListener('mousedown', sketch_mouseDown, false)
    doodleCanvas.value.addEventListener('mousemove', sketch_mouseMove, false)
    window.addEventListener('mouseup', sketch_mouseUp, false)
    window.addEventListener('keyup', handleLayerKeyup)
  }
}
function onDoodleTool() {
  if (doodleTool.value === 'free') {
    drawFree(ctx.value, mouseX.value, mouseY.value, doodleBrushSize.value)
  } else if (doodleTool.value === 'line') {
    drawLine(ctx.value, tempCtx.value, mouseX.value, mouseY.value, doodleBrushSize.value)
  } else if (doodleTool.value === 'rectangle') {
    drawRectangle(ctx.value, tempCtx.value, doodleBrushSize.value)
  } else if (doodleTool.value === 'circle') {
    drawCircle(ctx.value, tempCtx.value, doodleBrushSize.value)
  } else if (doodleTool.value === 'text') {
    emits('textPosition', { x: mouseX.value, y: mouseY.value })
  } else if (doodleTool.value === 'eraser') {
    drawEraser(ctx.value, mouseX.value, mouseY.value, doodleBrushSize.value)
  }
}
function sketch_mouseDown(e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()
  mouseDown.value = 1
  getMousePos(e)
  startX.value = mouseX.value
  startY.value = mouseY.value
  if (
    doodleTool.value === 'line' ||
    doodleTool.value === 'rectangle' ||
    doodleTool.value === 'circle'
  ) {
    // During mouse down, a temporary layer is used to continuously draw and clear during mouse movement
    // After the mouse button is released, the temporary drawing is cleared and the final drawing is produced on the main canvas
    tempCanvas.value = document.getElementById('doodle-canvas-temp')
    tempCanvas.value.width = window.innerWidth
    tempCanvas.value.height = window.innerHeight
    tempCtx.value = tempCanvas.value.getContext('2d')
  }
  onDoodleTool()
}
function sketch_mouseUp(e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()
  mouseDown.value = 0
  if (
    doodleTool.value === 'line' ||
    doodleTool.value === 'rectangle' ||
    doodleTool.value === 'circle'
  ) {
    onDoodleTool()
    tempCanvas.value = null
    tempCtx.value = null
  }
  lastX.value = -1
  lastY.value = -1
}
function sketch_mouseMove(e: MouseEvent) {
  e.preventDefault()
  getMousePos(e)
  emits('mousePosition', { x: mouseX.value, y: mouseY.value })
  if (mouseDown.value === 1) {
    onDoodleTool()
  }
}
watch(tempCanvas, () => {
  emits('showTempCanvas', tempCanvas.value ? true : false)
})
onMounted(() => {
  initializeDoodle()
})
onUnmounted(() => {
  doodleCanvas.value?.removeEventListener('mousedown', sketch_mouseDown, false)
  doodleCanvas.value?.removeEventListener('mousemove', sketch_mouseMove, false)
  window.removeEventListener('mouseup', sketch_mouseUp, false)
  window.removeEventListener('keyup', handleLayerKeyup)
})
defineExpose({
  drawImage
})
</script>
<style lang="scss" scoped>
.doodle-canvas {
  cursor: none;
  position: absolute;
  z-index: 1001;
  &__text {
    cursor: text;
  }
}
.doodle-canvas-temp {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1002;
}
</style>
